import {
  ArrowNarrowRightIcon,
  CheckIcon,
  GSFlame,
  Image05Icon,
  ImageCheckIcon,
  MessageTextCircleIcon,
  UploadCloudIcon,
  Users02Icon,
  VideoRecorderIcon,
} from 'assets/icons';
import { contactLink } from 'config/constants';
import { classnames } from 'helpers/utils';
import {
  NewVaultAction,
  VaultNewStateType,
} from 'pages/vault-new/vault-new-reducer';
import { formatPrice, sizeLabel } from 'pages/vault-new/plan-format';
import { Dispatch, useState } from 'react';
import styles from './step-plan.module.scss';

type StepPlanProps = {
  plans: StoragePlanType[];
  loading: boolean;
  newVaultState: VaultNewStateType;
  newVaultDispatch: Dispatch<NewVaultAction>;
};

// The design leads with Forever Storage, so `long_term` comes first.
const KINDS: StoragePlanKind[] = ['long_term', 'temporary'];

const kindCopy: Record<StoragePlanKind, { label: string; blurb: string }> = {
  long_term: {
    label: 'Forever Storage',
    blurb: 'Pay once. Own a family record that lasts forever.',
  },
  temporary: {
    label: 'Annual',
    blurb: 'Pay by the year. Make it permanent when ready.',
  },
};

// The size the design flags as "Recommended" and shows in the capacity card
// until the user picks one.
const RECOMMENDED_STORAGE_GB = 250;

const INCLUDED_IN_EVERY_VAULT = [
  { Icon: Users02Icon, label: 'Unlimited collaborators' },
  { Icon: VideoRecorderIcon, label: 'Interview & recording tools' },
  { Icon: ImageCheckIcon, label: 'Original-quality preservation' },
  { Icon: UploadCloudIcon, label: 'Imports from Drive, Dropbox, iCloud' },
];

// The API's media estimate is display copy such as
// "≈ 60,000 photos or 60 hours HD video"; split it into the two figures the
// capacity card shows side by side.
const parseEstimate = (estimate?: string) => {
  if (!estimate) return null;
  const photos = estimate.match(/([\d,.]+)\s*photos/i)?.[1];
  const hours = estimate.match(/([\d,.]+)\s*hours?/i)?.[1];
  if (!photos || !hours) return null;
  return { photos: `~${photos}`, hours: `~${hours} hours` };
};

// Step 2 of vault creation: pick a storage plan. A Forever Storage / Annual
// folder tab switches the price grid; the right column describes what the
// highlighted size holds and what every vault includes.
const StepPlan: React.FC<StepPlanProps> = ({
  plans,
  loading,
  newVaultState,
  newVaultDispatch,
}) => {
  const { planSelected } = newVaultState;
  const [kindTab, setKindTab] = useState<StoragePlanKind>(
    planSelected?.kind ?? 'long_term',
  );

  const visiblePlans = plans.filter((plan) => plan.kind === kindTab);
  const recommended = visiblePlans.find(
    (plan) => plan.storageGb === RECOMMENDED_STORAGE_GB,
  );
  // The capacity card follows the selection, else the recommended size.
  const featuredPlan = planSelected ?? recommended ?? visiblePlans[0] ?? null;
  const estimate = parseEstimate(featuredPlan?.mediaEstimate);

  const handleKindChange = (kind: StoragePlanKind) => {
    if (kind === kindTab) return;
    setKindTab(kind);

    // Keep the chosen size when switching between Forever Storage and Annual.
    if (planSelected) {
      const sameSize = plans.find(
        (plan) =>
          plan.kind === kind && plan.storageGb === planSelected.storageGb,
      );
      if (sameSize) newVaultDispatch({ type: 'PLAN_SELECTED', plan: sameSize });
    }
  };

  const showKindTab = (kind: StoragePlanKind) => {
    const isActive = kind === kindTab;

    return (
      <button
        key={kind}
        type="button"
        role="tab"
        aria-selected={isActive}
        className={classnames(styles.folderTab, {
          [styles.folderTabActive]: isActive,
        })}
        onClick={() => handleKindChange(kind)}
      >
        <span className={styles.folderTabInner}>
          <span
            className={classnames(styles.check, {
              [styles.checkChecked]: isActive,
            })}
          >
            {isActive && <CheckIcon />}
          </span>
          <span className={styles.folderTabContent}>
            <span className={styles.folderTabTitle}>
              {kindCopy[kind].label}
            </span>
            <span className={styles.folderTabBlurb}>
              {kindCopy[kind].blurb}
            </span>
          </span>
        </span>
      </button>
    );
  };

  const showPlanCard = (plan: StoragePlanType) => {
    const isSelected = planSelected?.id === plan.id;
    const isRecommended = plan.storageGb === RECOMMENDED_STORAGE_GB;

    return (
      <button
        key={plan.id}
        type="button"
        role="radio"
        aria-checked={isSelected}
        // A plan with no Stripe price behind it can't be charged for, so it is
        // shown (with its fallback price) but not selectable.
        disabled={!plan.purchasable}
        title={plan.purchasable ? undefined : 'Currently unavailable'}
        className={classnames(styles.planCard, {
          [styles.planCardSelected]: isSelected,
          [styles.planCardUnavailable]: !plan.purchasable,
        })}
        onClick={() => newVaultDispatch({ type: 'PLAN_SELECTED', plan })}
      >
        {isRecommended && (
          <span className={styles.recommendedBadge}>Recommended</span>
        )}
        <span className={styles.planSize}>{sizeLabel(plan.storageGb)}</span>
        <span className={styles.planPrice}>
          {formatPrice(plan.price, plan.currency)}
        </span>
      </button>
    );
  };

  const showCapacityCard = () => {
    if (!featuredPlan) return null;

    return (
      <div className={styles.capacityCard}>
        <div className={styles.capacityHeading}>
          <p className={styles.capacitySize}>
            {sizeLabel(featuredPlan.storageGb)}
          </p>
          <p className={styles.capacityLabel}>Holds approximately:</p>
        </div>

        {estimate ? (
          <div className={styles.capacityStats}>
            <div className={styles.capacityStat}>
              <span className={styles.featuredIcon}>
                <Image05Icon />
              </span>
              <span className={styles.capacityFigure}>{estimate.photos}</span>
              <span className={styles.capacityUnit}>photos</span>
            </div>
            <div className={styles.capacityOr}>
              <span className={styles.capacityOrLine} />
              <span className={styles.capacityOrText}>or</span>
              <span className={styles.capacityOrLine} />
            </div>
            <div className={styles.capacityStat}>
              <span className={styles.featuredIcon}>
                <VideoRecorderIcon />
              </span>
              <span className={styles.capacityFigure}>{estimate.hours}</span>
              <span className={styles.capacityUnit}>HD video</span>
            </div>
          </div>
        ) : (
          featuredPlan.mediaEstimate && (
            <p className={styles.capacityNote}>{featuredPlan.mediaEstimate}</p>
          )
        )}

        <p className={styles.capacityNote}>
          Other content like documents, audio, and written stories use much
          less.
        </p>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.columnLeft}>
        <div className={styles.tabs} role="tablist">
          {KINDS.map(showKindTab)}
        </div>

        <div className={styles.folder}>
          <div className={styles.banner}>
            <GSFlame className={styles.bannerLogo} />
            <p className={styles.bannerText}>
              <span className={styles.bannerStrong}>Forever Storage</span>
              {' costs less than annual after about ~5 years.'}
            </p>
            <button type="button" className={styles.bannerLink}>
              Learn how we guarantee forever
              <ArrowNarrowRightIcon />
            </button>
          </div>

          <div
            className={styles.plans}
            role="radiogroup"
            aria-label="Storage plan"
          >
            {loading && <p className={styles.plansMessage}>Loading plans…</p>}

            {!loading && visiblePlans.length === 0 && (
              <p className={styles.plansMessage}>
                Plans couldn&apos;t be loaded right now. Please try again in a
                moment.
              </p>
            )}

            {!loading && visiblePlans.map(showPlanCard)}
          </div>

          <button
            type="button"
            className={styles.needMore}
            onClick={() => window.open(contactLink, '_blank', 'noopener')}
          >
            Need more space? Get in touch
            <MessageTextCircleIcon />
          </button>
        </div>
      </div>

      <div className={styles.columnRight}>
        {showCapacityCard()}

        <div className={styles.included}>
          <p className={styles.includedTitle}>Every Vault includes:</p>
          <ul className={styles.includedList}>
            {INCLUDED_IN_EVERY_VAULT.map(({ Icon, label }) => (
              <li key={label} className={styles.includedItem}>
                <Icon className={styles.includedIcon} />
                <span>{label}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export { StepPlan };
